import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, FileCheck2, AlertTriangle } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Btn, Tag, EmptyState } from "@/components/kit";
import { api } from "@/lib/api";

const TABS = [
  { key: "allocation", label: "Allocation import", cols: "customer_code, registration, driver_employee_reference, driver_name, driver_mobile, driver_email, allocation_start_date, allocation_end_date, allocation_status" },
  { key: "booking_update", label: "Booking update import", cols: "booking_reference, registration, appointment_date, internal_status, public_status, garage_name, public_update, expected_completion, next_update_due, ready_for_collection" },
  { key: "f12", label: "Legacy F12 notes", cols: "Reg No\\Incident\\Cust, AE, Note Type, Date & Time, Summary, Details, Customer — historic Venson Contract Management F12 export. Raw notes are preserved verbatim and tagged \"FW F12 Import\"; AI classification is additive only." },
  { key: "ooh", label: "OOH i247 daily", cols: "Worksheet Id, Category, Job, Date, Time, Reg No, Vehicle, Driver, DC Code, DC Name, Job Address, Driver Mobile No, Mileage, Driver Email, Comments, Action Required — i247 out-of-hours daily worksheet. Matched by DC Code → customer → pod. Rows with Action Required = Y are routed to the pod queue as OOH follow-ups; the raw narrative is preserved in vehicle history." },
];

const TONE = { unchanged: "muted", new: "green", driver_change: "clay", closed: "amber", conflict: "red", invalid: "red", matched: "green", unmatched: "amber" };

export default function Imports() {
  const [tab, setTab] = useState("allocation");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const kind = tab;
  const meta = TABS.find((t) => t.key === tab);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setPreview(null);
    try {
      const res = await api.importPreview(kind, file);
      setPreview(res);
      toast.success("File parsed. Review before applying.");
    } catch (e) { toast.error(e.message || "Could not parse file"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.importApply(kind, preview.batch_id);
      const q = res.queued_for_review ? ` ${res.queued_for_review} unmatched row(s) queued for review.` : "";
      const rt = res.routed ? ` ${res.routed} routed to pod queue.` : "";
      toast.success(`Applied ${res.applied_rows} row${res.applied_rows === 1 ? "" : "s"}. Historical records preserved.${rt}${q}`);
      setPreview(null);
    } catch (e) { toast.error(e.message || "Apply failed"); }
    finally { setBusy(false); }
  };

  const summary = preview?.summary || {};

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Data imports</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-5">CSV import &amp; preview</h1>

      <div className="flex gap-2 mb-5">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`import-tab-${t.key}`} onClick={() => { setTab(t.key); setPreview(null); }}
            className={`h-9 px-4 text-sm rounded-[3px] border ${tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Panel className="p-5">
        <div className="text-sm text-muted-foreground">Expected columns</div>
        <code className="block text-xs mt-1 vda-reg break-words">{meta.cols}</code>
        <label className="mt-4 inline-flex items-center gap-2 cursor-pointer bg-primary text-primary-foreground h-11 px-4 rounded-[3px] text-sm font-medium hover:bg-primary/90">
          <Upload className="h-4 w-4" /> Choose CSV file
          <input data-testid="import-file" type="file" accept=".csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
        <p className="mt-2 text-xs text-muted-foreground">Nothing is applied until you confirm. Historical allocation records are never deleted.</p>
      </Panel>

      {busy && !preview && <p className="mt-4 text-sm text-muted-foreground">Processing…</p>}

      {preview && (
        <div className="mt-5 space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(summary).map(([k, v]) => (
              <Panel key={k} className="p-3">
                <div className="vda-tick text-muted-foreground">{k.replace(/_/g, " ")}</div>
                <div className="text-2xl font-semibold tabular-nums text-primary mt-1">{v}</div>
              </Panel>
            ))}
          </div>

          <Panel className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                    <th className="px-3 py-2 font-medium">#</th>
                    <th className="px-3 py-2 font-medium">Match</th>
                    <th className="px-3 py-2 font-medium">Detail</th>
                    <th className="px-3 py-2 font-medium">Row data</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className="border-t border-border" data-testid={`import-row-${r.row_number}`}>
                      <td className="px-3 py-2 text-muted-foreground">{r.row_number}</td>
                      <td className="px-3 py-2"><Tag tone={TONE[r.match_status] || "muted"}>{r.match_status}</Tag></td>
                      <td className="px-3 py-2">{r.validation_message}</td>
                      <td className="px-3 py-2 vda-reg text-xs text-muted-foreground truncate max-w-[300px]">{Object.values(r.raw_data).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="flex items-center gap-3">
            <Btn data-testid="import-apply" onClick={apply} loading={busy}><FileCheck2 className="h-4 w-4" /> Confirm &amp; apply</Btn>
            <Btn variant="outline" onClick={() => setPreview(null)}>Discard</Btn>
            {(summary.invalid > 0 || summary.conflict > 0) && (
              <span className="text-sm text-[hsl(var(--vda-amber))] flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Invalid / conflicting rows will be skipped.</span>
            )}
          </div>
        </div>
      )}

      {!preview && !busy && <div className="mt-4"><EmptyState title="No file loaded" hint="Choose a CSV to see a validated preview." /></div>}
    </StaffShell>
  );
}
